import {
  mergeClasses, Button, Caption1, Text, makeStyles } from '@fluentui/react-components';
import { Link } from 'react-router-dom';
import { StatusDot, complianceTokenFor, shellMedia, shellMetrics, tokens } from '@vigeo/ui';
import type { OfferGroup, OfferRow, UnreadableOfferRow } from '@vigeo/shared';
import { day, keyDate, offerDetail, offerGroupCopy, offerSummary, offersUnavailableCopy, rowAction, statusBadge, type BadgeTone } from './model.js';

const useStyles = makeStyles({
  table: {
    display: 'flex', flexDirection: 'column', minWidth: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    overflow: 'hidden',
  },
  group: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL} ${tokens.spacingVerticalXS}`,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  firstGroup: { borderTop: 'none' },
  groupTitle: { display: 'flex', alignItems: 'baseline', gap: tokens.spacingHorizontalXS },
  h: { margin: 0, fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase400, fontWeight: tokens.fontWeightSemibold },
  row: {
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: tokens.spacingHorizontalL, minWidth: 0,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  candidate: {
    display: 'flex', flexDirection: 'column', minWidth: '13rem', flexBasis: '16rem', flexGrow: 1,
    /*
      The name is a LINK, and on a phone it measured 324×20 — wide enough and nowhere near tall
      enough. The `action` column below already carries this floor and says why; the name did not,
      because nothing measured this screen until #331 added it to the sweep.

      On the link itself rather than the column: the column also holds the role caption, so a floor
      here would pad the cell without making the link any easier to hit.
    */
    [shellMedia.phone]: { '& a': { display: 'inline-flex', alignItems: 'center', minHeight: shellMetrics.touchTarget } },
  },
  offer: { display: 'flex', flexDirection: 'column', minWidth: '11rem', flexBasis: '16rem', flexGrow: 1 },
  status: { display: 'flex', minWidth: '9rem', flexBasis: '11rem' },
  when: { display: 'flex', flexDirection: 'column', minWidth: '11rem', flexBasis: '13rem' },
  // A 44px target on a phone (the touch-target gate), and never below the control height.
  action: { display: 'flex', justifyContent: 'flex-end', flexBasis: '8rem', flexGrow: 1, minHeight: '2.75rem', alignItems: 'center' },
  empty: { padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}` },
  /**
   * The frame's column header, above the first group. Same flex basis as a row so the labels sit
   * over the columns they name rather than near them.
   */
  headerRow: {
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: tokens.spacingHorizontalL, minWidth: 0,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorNeutralBackground2,
    /*
     * Hidden below tablet, the way CandidateTable and ReviewQueue already hide their wide-only
     * columns. The cells carry 9–13rem minimums, so at phone width every label wraps onto its own
     * line and the header becomes four orphan words above rows that have themselves stacked —
     * labelling nothing. A row is self-describing there; the header is not.
     */
    [shellMedia.belowTablet]: { display: 'none' },
  },
  headerCell: { fontWeight: tokens.fontWeightSemibold },
  footer: {
    display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'center',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorNeutralBackground2,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
});

/**
 * The frames draw an offer's status as a coloured dot and plain text, not a tinted pill, so the
 * tone maps to a compliance colour rather than to a Fluent Badge colour name.
 * `neutral` and `brand` are both "nothing is wrong yet", which the frames draw grey.
 */
const OFFER_TONE_COLOR: Record<BadgeTone, string> = {
  neutral: tokens.colorNeutralForeground3,
  brand: tokens.colorNeutralForeground3,
  good: complianceTokenFor.good,
  warning: complianceTokenFor.warning,
};

/** Offers live on the person's Hiring section until the offer screens land (#260–#263). */
const personHref = (row: OfferRow) => `/app/people/${row.personId}?section=hiring`;

/**
 * Where the row's button goes (#261, #262).
 *
 * "Review" and "Send" have screens of their own now, and this list is the only place either is
 * reached from. Anything else — preparing a first draft, viewing a signed offer — still lands on
 * the record's Hiring section, which is where those live.
 */
function actionHref(row: OfferRow): string {
  const id = row.recordId;
  if (id === null) return personHref(row);
  if (row.group === 'drafts') return `/app/people/${row.personId}/offer/${encodeURIComponent(id)}/review`;
  if (row.group === 'ready-to-send') return `/app/people/${row.personId}/offer/send?record=${encodeURIComponent(id)}`;
  return personHref(row);
}

function OfferRowView({ row, roleLabel, canEdit }: { readonly row: OfferRow; readonly roleLabel: string | null; readonly canEdit: boolean }) {
  const styles = useStyles();
  const badge = statusBadge(row);
  const when = keyDate(row);
  const action = rowAction(row);
  return (
    <div className={styles.row}>
      <div className={styles.candidate}>
        <Link to={personHref(row)}>{row.candidateName}</Link>
        {roleLabel === null ? null : <Caption1>{roleLabel}</Caption1>}
      </div>
      <div className={styles.offer}>
        <Text>{offerSummary(row)}</Text>
        <Caption1>{offerDetail(row)}</Caption1>
      </div>
      <div className={styles.status}>
        <StatusDot color={OFFER_TONE_COLOR[badge.tone]}>{badge.label}</StatusDot>
      </div>
      <div className={styles.when}>
        {when === null ? <Text>—</Text> : (
          <>
            <Text>{when.primary}</Text>
            {when.secondary === null ? null : <Caption1>{when.secondary}</Caption1>}
          </>
        )}
      </div>
      <div className={styles.action}>
        {/* A Viewer is never offered what the server will refuse. `Draft offer`, `Review` and
            `Send` all need `people.update`, so for anyone without it the row offers nothing at
            all rather than a control that fails or a disabled stand-in. */}
        {canEdit || !action.adminOnly ? <Button as="a" href={actionHref(row)}>{action.label}</Button> : null}
      </div>
    </div>
  );
}

/**
 * A candidate whose records could not be read (#299).
 *
 * Deliberately the SAME five columns as a real row, so the band reads as part of the list rather
 * than as an error panel bolted above it: this person is in hiring like everybody else, and the one
 * thing missing is where their offer stands. Reading is always allowed, so the action is a plain
 * link to the record and is shown to a Viewer too.
 */
function UnavailableRowView({ row, roleLabel }: { readonly row: UnreadableOfferRow; readonly roleLabel: string | null }) {
  const styles = useStyles();
  return (
    <div className={styles.row}>
      <div className={styles.candidate}>
        <Link to={`/app/people/${row.personId}?section=hiring`}>{row.candidateName}</Link>
        {roleLabel === null ? null : <Caption1>{roleLabel}</Caption1>}
      </div>
      <div className={styles.offer}>
        <Text>{offersUnavailableCopy.summary}</Text>
        <Caption1>{offersUnavailableCopy.detail}</Caption1>
      </div>
      <div className={styles.status}>
        {/*
          A dot, like every other status in this table. #317 added this row while row 0.2 was
          replacing the tinted `Badge` everywhere, so it arrived carrying the one treatment the
          approved frames do not use; the merge of the two branches is where that shows up.
        */}
        <StatusDot color={OFFER_TONE_COLOR.warning}>Unavailable</StatusDot>
      </div>
      <div className={styles.when}>
        {/* Null exactly when the person document itself was the unreadable one. */}
        <Text>{row.addedAt === null ? '—' : `Added ${day(row.addedAt)}`}</Text>
      </div>
      <div className={styles.action}>
        <Button as="a" href={`/app/people/${row.personId}?section=hiring`}>{offersUnavailableCopy.action}</Button>
      </div>
    </div>
  );
}

export interface OffersTableProps {
  readonly rows: readonly OfferRow[];
  readonly counts: Record<OfferGroup, number>;
  /**
   * Candidates whose records could not be read. REQUIRED, not defaulted: a caller that forgets
   * these drops a person from the one screen meant to show every offer in flight, which is the
   * failure #299 calls worse than the 500 it replaced.
   */
  readonly unavailable: readonly UnreadableOfferRow[];
  /** Which group to show; every group when null. */
  readonly only: OfferGroup | null;
  readonly roleLabels: ReadonlyMap<string, string>;
  /** Whether the reader may act on an offer at all. A Viewer may read every row and act on none. */
  readonly canEdit: boolean;
}

export function OffersTable({ rows, counts, unavailable, only, roleLabels, canEdit }: OffersTableProps) {
  const styles = useStyles();
  const shown = offerGroupCopy.filter((group) => only === null || group.id === only);
  const roleOf = (row: { readonly jobProfileKeys: readonly string[] }) =>
    row.jobProfileKeys.map((key) => roleLabels.get(key) ?? key).join(', ') || null;
  /**
   * SHOWN WHATEVER THE FILTER IS. Where these people's offers stand is exactly what could not be
   * read, so "Signed" cannot honestly exclude them and neither can any other group — filtering them
   * out would be the screen deciding they are not in the group it is showing. First, because an
   * anomaly nobody scrolls to is an anomaly nobody fixes.
   */
  const degraded = unavailable.length > 0;

  return (
    <div className={styles.table}>
      {/*
        The frame names the columns once, above the first group — Candidate, Offer, Status, Key
        date. The action column is deliberately unlabelled there, and is here too.
      */}
      {/*
        aria-hidden, not role="presentation". A div has no semantics to remove, and
        presentational-children inheritance applies to roles like table and list — so the four
        labels stayed fully in the accessibility tree and were read as loose words before the first
        group, associated with no cell. This is a flex layout, not a table, so there is no
        header-to-cell relationship to establish; each row already names its own fields.
      */}
      <div className={styles.headerRow} aria-hidden="true">
        {/*
          mergeClasses, NOT a template literal — the same Griffel trap this branch fixed on the role
          chips, reintroduced five lines from the fix. `Caption1` is a Fluent component, so it runs
          mergeClasses on the className it is given; everything after the second sequence hash is
          discarded, which dropped the semibold the frame's header is built from.
        */}
        <Caption1 className={mergeClasses(styles.candidate, styles.headerCell)}>Candidate</Caption1>
        <Caption1 className={mergeClasses(styles.offer, styles.headerCell)}>Offer</Caption1>
        <Caption1 className={mergeClasses(styles.status, styles.headerCell)}>Status</Caption1>
        <Caption1 className={mergeClasses(styles.when, styles.headerCell)}>Key date</Caption1>
        <span className={styles.action} />
      </div>
      {!degraded ? null : (
        <section aria-label={offersUnavailableCopy.title}>
          <div className={`${styles.group} ${styles.firstGroup}`}>
            <div className={styles.groupTitle}>
              <h2 className={styles.h}>{offersUnavailableCopy.title}</h2>
              <Caption1>{unavailable.length}</Caption1>
            </div>
            <Caption1>{offersUnavailableCopy.blurb}</Caption1>
          </div>
          {unavailable.map((row) => <UnavailableRowView key={row.personId} row={row} roleLabel={roleOf(row)} />)}
        </section>
      )}
      {shown.map((group, index) => {
        const inGroup = rows.filter((row) => row.group === group.id);
        /*
         * An empty group is left out entirely, as the frame does: "Ready to send (0)" appears in
         * the filter pills and has no section. The count is already on the pill, so a section
         * saying nobody is here adds a row of nothing between two real ones.
         *
         * UNLESS the reader asked for exactly that group — then the emptiness IS the answer, and
         * silently rendering nothing would look like a broken filter.
         */
        if (inGroup.length === 0 && only === null) return null;
        return (
          <section key={group.id} aria-label={group.title}>
            {/* The unavailable band, when there is one, is what sits flush against the header. */}
            <div className={index === 0 && !degraded ? `${styles.group} ${styles.firstGroup}` : styles.group}>
              <div className={styles.groupTitle}>
                <h2 className={styles.h}>{group.title}</h2>
                <Caption1>{counts[group.id]}</Caption1>
              </div>
              <Caption1>{group.blurb}</Caption1>
            </div>
            {inGroup.length === 0
              ? <div className={styles.empty}><Caption1>Nobody is here right now.</Caption1></div>
              : inGroup.map((row) => (
                /*
                 * KEYED ON THE OFFER, not on the person (#297). The list is one row per offer, so a
                 * candidate can appear in two groups — and while this keyed on `personId`, React
                 * treated a replacement offer as the same row as the original and reused its state.
                 * `recordId` is null only on a `to-prepare` row, of which a person has at most one,
                 * so falling back to `personId` there is still unique.
                 */
                <OfferRowView key={row.recordId ?? row.personId} row={row} roleLabel={roleOf(row)} canEdit={canEdit} />
              ))}
          </section>
        );
      })}
      <div className={styles.footer}>
        <Caption1>
          Withdrawn offers are hidden. A withdrawn offer disappears from the candidate’s portal and stays in their History.
        </Caption1>
      </div>
    </div>
  );
}
